// @givmo/app-charity — Board's officer operations over THIS console's door, the charity door.
//
// `@givmo/board` names no class (CBG-MOD2-WEB): it speaks only the organization wire, `org_ref` and
// the module vocabulary, and its `BoardOperations` port is typed against the `/board/{org_ref}`
// operations. This console keeps calling the charity door (`/charity-admin/board/charities/{id}/…`),
// which serves GCF and every charity in staging and production, so it supplies the port itself: one
// member per operation, each calling exactly its same-named generated charity operation and
// re-spelling the wire at this boundary — the `adaptCharityComplianceResponse` precedent.
//
// THE RE-SPELLING MIRRORS THE BACKEND'S ORGANIZATION TWIN, position for position, and nothing else:
//   * request — the path key `org_ref` → `charity_id`; a body's `scope: organization` → `charity` and
//     `recorded_by: organization_admin` → `charity_admin` (the backend's `BoardWire.stored`);
//   * response — on the root, every array element, and the structural children `data`, `hold`,
//     `version_in_force`, `versions` and `record`: `charity_id` → `org_ref`, `scope: charity` →
//     `organization`, `recorded_by: charity_admin` → `organization_admin` (`wire.identity`,
//     `wire.value`). Every other subtree is opaque and passes untouched: a record's `payload`, a
//     packet item's `filing_governance_snapshot` and its `feed`, notices, and the export manifest
//     whole — the backend sends those byte-identically on both doors. The annual disclosures' PDFs
//     (a year's responses, a paper form, one response; CBG-YR2-WEB1) are bytes, passed through opaque:
//     a Blob is an object, and walking one would hand the browser `{}`.
// An object carrying BOTH keys is a response this adapter cannot read honestly, so it throws.
// `charity-board-operations.test.ts` walks every member and pins each re-spelling;
// `charity-board-wire-census.test.ts` holds the positions to the committed OpenAPI snapshot's own
// charity and organization schemas.
//
// A seat's history and the release of its joined account (SEC2-BE1b's routes) graduated from their
// hatches at the one regeneration from landed `development`: every member is a generated operation.
//
// The DIRECTOR port is not this console's to re-spell: the director family has no class door, so the
// runtime carries the package's own `createOrgBoardDirectorOperations()` (contract §3.1). Nor is the
// CONNECTION port (core's `/org/{org_ref}/integrations`, CBG-CAL1): class-neutral org machinery, so
// the runtime carries `createOrgConnectionOperations()` as it is.

import { sdk } from '@givmo/api-client';
import * as query from '@givmo/api-client/query';
import {
  createOrgBoardDirectorOperations,
  createOrgConnectionOperations,
  type BoardDisclaimerTarget,
  type BoardHostVocab,
  type BoardOperations,
  type BoardRuntime,
  type BoardStanding,
} from '@givmo/board';
import { useOptionalCharityConsole } from '../../console/charity-console-context';

/** This console's word for the organization in Board's copy (Q5): "charity", as it always read. */
export const CHARITY_BOARD_VOCAB: BoardHostVocab = { orgNoun: 'charity' };

/** How this console's class addresses the disclaimer registry: the charity, as the read always did. */
export function charityBoardDisclaimerTarget(orgRef: string): BoardDisclaimerTarget {
  return { charity_id: orgRef };
}

/** Response values the charity door spells by class, per field → the module's spelling. */
const MODULE_VALUES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  scope: { charity: 'organization' },
  recorded_by: { charity_admin: 'organization_admin' },
};

/** The inverse, for a request body the module spelled. */
const DOOR_VALUES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  scope: { organization: 'charity' },
  recorded_by: { organization_admin: 'charity_admin' },
};

/** Where the organization twin nests another twinned model; everything else is opaque. */
export const STRUCTURAL_CHILDREN: ReadonlySet<string> = new Set([
  'data',
  'hold',
  'version_in_force',
  'versions',
  'record',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function respelled(
  field: string,
  value: unknown,
  table: Readonly<Record<string, Readonly<Record<string, string>>>>,
): unknown {
  const values = table[field];
  if (values === undefined || typeof value !== 'string') return value;
  return Object.hasOwn(values, value) ? values[value] : value;
}

/** A charity-door response in the organization wire, at exactly the twin's positions. */
export function adaptCharityBoardResponse(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(adaptCharityBoardResponse);
  if (!isRecord(value)) return value;
  if (Object.hasOwn(value, 'charity_id') && Object.hasOwn(value, 'org_ref')) {
    throw new Error('board adapter: a response carries both charity_id and org_ref');
  }
  const adapted: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (key === 'charity_id') adapted.org_ref = entry;
    else if (STRUCTURAL_CHILDREN.has(key)) adapted[key] = adaptCharityBoardResponse(entry);
    else adapted[key] = respelled(key, entry, MODULE_VALUES);
  }
  return adapted;
}

/** A module request in the charity door's wire: the path key, and a body's class-spelled values. */
export function charityBoardRequest(options: unknown): unknown {
  if (!isRecord(options)) return options;
  let request = options;
  const { path, body } = options;
  if (isRecord(path) && Object.hasOwn(path, 'org_ref')) {
    const { org_ref, ...rest } = path;
    request = { ...request, path: { ...rest, charity_id: org_ref } };
  }
  if (isRecord(body)) {
    const spelled = Object.fromEntries(
      Object.entries(body).map(([field, value]) => [field, respelled(field, value, DOOR_VALUES)]),
    );
    if (Object.entries(spelled).some(([field, value]) => value !== body[field])) {
      request = { ...request, body: spelled };
    }
  }
  return request;
}

// The generated charity functions are typed by their own door; these three wrappers take them
// untyped and the port's member types are asserted once, below, where the member SET is checked.
/* eslint-disable @typescript-eslint/no-explicit-any */
type Generated = (options: any) => any;

function charityCall(call: Generated, { opaque = false }: { opaque?: boolean } = {}) {
  return async (options: unknown) => {
    const result = await call(charityBoardRequest(options));
    if (opaque || !isRecord(result) || !Object.hasOwn(result, 'data')) return result;
    return { ...result, data: adaptCharityBoardResponse(result.data) };
  };
}

function charityQueryOptions(factory: Generated) {
  return (options: unknown) => {
    const base = factory(charityBoardRequest(options));
    const queryFn = base.queryFn as (context: unknown) => Promise<unknown>;
    return {
      ...base,
      queryFn: async (context: unknown) => adaptCharityBoardResponse(await queryFn(context)),
    };
  };
}

function charityQueryKey(factory: Generated) {
  return (options: unknown) => factory(charityBoardRequest(options));
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Board's port for this console: every member over the charity door, re-spelled at the boundary. */
export function createCharityBoardOperations(): BoardOperations {
  const members: { [Member in keyof BoardOperations]: unknown } = {
    // CBG-YR6-REG and -AUTH: a board's committees — its registry, its members and its rules. Their
    // models carry no class-spelled field, so the door's answer is the module's as it stands.
    officerAddBoardCommitteeMember: charityCall(sdk.charityBoardAddBoardCommitteeMember),
    officerCorrectBoardCommitteeDissolution: charityCall(
      sdk.charityBoardCorrectBoardCommitteeDissolution,
    ),
    officerCreateBoardCommittee: charityCall(sdk.charityBoardCreateBoardCommittee),
    officerDissolveBoardCommittee: charityCall(sdk.charityBoardDissolveBoardCommittee),
    officerEndBoardCommitteeMember: charityCall(sdk.charityBoardEndBoardCommitteeMember),
    officerLinkBoardCommitteeCharter: charityCall(sdk.charityBoardLinkBoardCommitteeCharter),
    officerRecordBoardCommitteeRule: charityCall(sdk.charityBoardRecordBoardCommitteeRule),
    officerUpdateBoardCommittee: charityCall(sdk.charityBoardUpdateBoardCommittee),
    officerUpdateBoardCommitteeMember: charityCall(sdk.charityBoardUpdateBoardCommitteeMember),
    // CBG-YR6-CONFIRM: a committee's practice, and the committees' decisions suggested on a board
    // meeting — its press, its dismissal, its restore (and its read, among the query factories).
    // Their models carry no class-spelled field either.
    officerSetBoardCommitteeConfirmation: charityCall(
      sdk.charityBoardSetBoardCommitteeConfirmation,
    ),
    officerAddBoardCommitteeSuggestionItems: charityCall(
      sdk.charityBoardAddBoardCommitteeSuggestionItems,
    ),
    officerDismissBoardCommitteeSuggestion: charityCall(
      sdk.charityBoardDismissBoardCommitteeSuggestion,
    ),
    officerRestoreBoardCommitteeSuggestion: charityCall(
      sdk.charityBoardRestoreBoardCommitteeSuggestion,
    ),
    // CBG-YR6-OUTSIDE-WIDEN: the Owner adds someone who serves on a committee to the board. The
    // answer is the seat, re-spelled like every roster read.
    officerAddBoardMemberToBoard: charityCall(sdk.charityBoardAddBoardMemberToBoard),
    // CBG-YR3-ANNUAL: the annual meeting's election suggestion — its press, its dismissal, its
    // restore (and its read, among the query factories).
    officerAddBoardTermSuggestionItems: charityCall(sdk.charityBoardAddBoardTermSuggestionItems),
    officerDismissBoardTermSuggestion: charityCall(sdk.charityBoardDismissBoardTermSuggestion),
    officerRestoreBoardTermSuggestion: charityCall(sdk.charityBoardRestoreBoardTermSuggestion),
    // CBG-YR3-TERMS: the board's own director classes, and a seat's terms.
    officerCreateBoardDirectorClass: charityCall(sdk.charityBoardCreateBoardDirectorClass),
    officerUpdateBoardDirectorClass: charityCall(sdk.charityBoardUpdateBoardDirectorClass),
    officerRetireBoardDirectorClass: charityCall(sdk.charityBoardRetireBoardDirectorClass),
    officerRecordBoardMemberTerm: charityCall(sdk.charityBoardRecordBoardMemberTerm),
    officerUpdateBoardMemberTerm: charityCall(sdk.charityBoardUpdateBoardMemberTerm),
    officerRemoveBoardMemberTerm: charityCall(sdk.charityBoardRemoveBoardMemberTerm),
    // CBG-YR3-ELECT: an election's result put on the roster.
    officerRecordBoardRosterResult: charityCall(sdk.charityBoardRecordBoardRosterResult),
    officerAdoptBoardCalendarSettings: charityCall(sdk.charityBoardAdoptBoardCalendarSettings),
    officerAdoptBoardRetentionPolicy: charityCall(sdk.charityBoardAdoptBoardRetentionPolicy),
    officerApplyBoardAgendaTemplate: charityCall(sdk.charityBoardApplyBoardAgendaTemplate),
    officerApplyBoardCalendarSettings: charityCall(sdk.charityBoardApplyBoardCalendarSettings),
    officerApproveBoardMinutes: charityCall(sdk.charityBoardApproveBoardMinutes),
    officerApproveBoardRecord: charityCall(sdk.charityBoardApproveBoardRecord),
    // CBG-RC10: a closed session's minutes out for the signatures of the directors who were there.
    officerAskBoardMinutesApprovalSignatures: charityCall(
      sdk.charityBoardAskBoardMinutesApprovalSignatures,
    ),
    officerAdoptBoardOfficerDuties: charityCall(sdk.charityBoardAdoptBoardOfficerDuties),
    officerCancelBoardCoiCycle: charityCall(sdk.charityBoardCancelBoardCoiCycle),
    officerCancelBoardMeeting: charityCall(sdk.charityBoardCancelBoardMeeting),
    officerCirculateBoardWrittenConsent: charityCall(sdk.charityBoardCirculateBoardWrittenConsent),
    officerCloseBoardCoiCycle: charityCall(sdk.charityBoardCloseBoardCoiCycle),
    officerCloseBoardImportBatch: charityCall(sdk.charityBoardCloseBoardImportBatch),
    officerConfirmBoardDocument: charityCall(sdk.charityBoardConfirmBoardDocument),
    officerCreateBoardAgendaItem: charityCall(sdk.charityBoardCreateBoardAgendaItem),
    officerCreateBoardMeeting: charityCall(sdk.charityBoardCreateBoardMeeting),
    officerCreateBoardMeetingSegment: charityCall(sdk.charityBoardCreateBoardMeetingSegment),
    officerCreateBoardMember: charityCall(sdk.charityBoardCreateBoardMember),
    officerCreateBoardMinutesApprovalConsent: charityCall(
      sdk.charityBoardCreateBoardMinutesApprovalConsent,
    ),
    officerCreateBoardMotion: charityCall(sdk.charityBoardCreateBoardMotion),
    officerCreateBoardOfficerTitle: charityCall(sdk.charityBoardCreateBoardOfficerTitle),
    officerCreateBoardPacket: charityCall(sdk.charityBoardCreateBoardPacket),
    officerCreateBoardPacketItem: charityCall(sdk.charityBoardCreateBoardPacketItem),
    officerCreateBoardRecordDraft: charityCall(sdk.charityBoardCreateBoardRecordDraft),
    officerCreateBoardWrittenConsent: charityCall(sdk.charityBoardCreateBoardWrittenConsent),
    officerDeleteBoardAgendaItem: charityCall(sdk.charityBoardDeleteBoardAgendaItem),
    officerDeleteBoardConflictLink: charityCall(sdk.charityBoardDeleteBoardConflictLink),
    officerDeleteBoardMeetingSegment: charityCall(sdk.charityBoardDeleteBoardMeetingSegment),
    officerDeleteBoardMotion: charityCall(sdk.charityBoardDeleteBoardMotion),
    officerDeleteBoardPacketItem: charityCall(sdk.charityBoardDeleteBoardPacketItem),
    officerDeleteBoardRecord: charityCall(sdk.charityBoardDeleteBoardRecord),
    officerDeleteBoardRecordChain: charityCall(sdk.charityBoardDeleteBoardRecordChain),
    officerDiscardBoardPacket: charityCall(sdk.charityBoardDiscardBoardPacket),
    officerDiscardBoardRecordDraft: charityCall(sdk.charityBoardDiscardBoardRecordDraft),
    officerDistributeBoardPacket: charityCall(sdk.charityBoardDistributeBoardPacket),
    officerExportBoardMinuteBook: charityCall(sdk.charityBoardExportBoardMinuteBook, {
      opaque: true,
    }),
    officerExportBoardMinuteBookDocument: charityCall(
      sdk.charityBoardExportBoardMinuteBookDocument,
    ),
    officerGetBoardCalendarSettings: charityCall(sdk.charityBoardGetBoardCalendarSettings),
    officerGetBoardCoiCycle: charityCall(sdk.charityBoardGetBoardCoiCycle),
    officerGetBoardCoiCycleDocument: charityCall(sdk.charityBoardGetBoardCoiCycleDocument, {
      opaque: true,
    }),
    officerGetBoardCoiPaperForm: charityCall(sdk.charityBoardGetBoardCoiPaperForm, {
      opaque: true,
    }),
    officerGetBoardCoiPersonQuestionnaire: charityCall(
      sdk.charityBoardGetBoardCoiPersonQuestionnaire,
    ),
    officerGetBoardCoiQuestionnaire: charityCall(sdk.charityBoardGetBoardCoiQuestionnaire),
    officerGetBoardCoiResponse: charityCall(sdk.charityBoardGetBoardCoiResponse),
    officerGetBoardCoiResponseChanges: charityCall(sdk.charityBoardGetBoardCoiResponseChanges),
    officerGetBoardCoiResponseDocument: charityCall(sdk.charityBoardGetBoardCoiResponseDocument, {
      opaque: true,
    }),
    officerGetBoardConsentTemplate: charityCall(sdk.charityBoardGetBoardConsentTemplate),
    officerGetBoardDocumentDownloadUrl: charityCall(sdk.charityBoardGetBoardDocumentDownloadUrl),
    officerGetBoardGoverningDocumentTemplate: charityCall(
      sdk.charityBoardGetBoardGoverningDocumentTemplate,
    ),
    officerGetBoardImportBatch: charityCall(sdk.charityBoardGetBoardImportBatch),
    officerGetBoardLegalHoldReport: charityCall(sdk.charityBoardGetBoardLegalHoldReport),
    officerGetBoardMeetingCalendar: charityCall(sdk.charityBoardGetBoardMeetingCalendar),
    officerGetBoardMeetingInvitations: charityCall(sdk.charityBoardGetBoardMeetingInvitations),
    officerGetBoardMember: charityCall(sdk.charityBoardGetBoardMember),
    officerGetBoardMinutesApprovalConsentForm: charityCall(
      sdk.charityBoardGetBoardMinutesApprovalConsentForm,
    ),
    officerGetBoardOfficerStatement: charityCall(sdk.charityBoardGetBoardOfficerStatement),
    officerGetBoardRecord: charityCall(sdk.charityBoardGetBoardRecord),
    officerGetBoardWrittenConsent: charityCall(sdk.charityBoardGetBoardWrittenConsent),
    officerImportBoardRecord: charityCall(sdk.charityBoardImportBoardRecord),
    officerIssueBoardDocumentUploadUrl: charityCall(sdk.charityBoardIssueBoardDocumentUploadUrl),
    officerListBoardCalendarConnections: charityCall(sdk.charityBoardListBoardCalendarConnections),
    officerListBoardCalendarSenders: charityCall(sdk.charityBoardListBoardCalendarSenders),
    officerListBoardCoiCycles: charityCall(sdk.charityBoardListBoardCoiCycles),
    officerListBoardCoiQuestionnaires: charityCall(sdk.charityBoardListBoardCoiQuestionnaires),
    officerListBoardConsentTemplates: charityCall(sdk.charityBoardListBoardConsentTemplates),
    officerListBoardGoverningDocumentTemplates: charityCall(
      sdk.charityBoardListBoardGoverningDocumentTemplates,
    ),
    officerListBoardGoverningDocuments: charityCall(sdk.charityBoardListBoardGoverningDocuments),
    officerListBoardImportBatchRecords: charityCall(sdk.charityBoardListBoardImportBatchRecords),
    officerListBoardImportBatches: charityCall(sdk.charityBoardListBoardImportBatches),
    officerListBoardMeetingEvents: charityCall(sdk.charityBoardListBoardMeetingEvents),
    officerListBoardMemberHistory: charityCall(sdk.charityBoardListBoardMemberHistory),
    officerListBoardMembers: charityCall(sdk.charityBoardListBoardMembers),
    officerListBoardRecordEvents: charityCall(sdk.charityBoardListBoardRecordEvents),
    officerListBoardRecords: charityCall(sdk.charityBoardListBoardRecords),
    officerListBoardWrittenConsents: charityCall(sdk.charityBoardListBoardWrittenConsents),
    officerMintBoardAccessLink: charityCall(sdk.charityBoardMintBoardAccessLink),
    officerMoveBoardCalendarMeetings: charityCall(sdk.charityBoardMoveBoardCalendarMeetings),
    officerMoveBoardMeetingInvitations: charityCall(sdk.charityBoardMoveBoardMeetingInvitations),
    officerOpenBoardCoiCycle: charityCall(sdk.charityBoardOpenBoardCoiCycle),
    officerOpenBoardImportBatch: charityCall(sdk.charityBoardOpenBoardImportBatch),
    officerPlaceBoardLegalHold: charityCall(sdk.charityBoardPlaceBoardLegalHold),
    officerPublishBoardPacket: charityCall(sdk.charityBoardPublishBoardPacket),
    officerRecordBoardAttendanceIntentOnBehalf: charityCall(
      sdk.charityBoardRecordBoardAttendanceIntentOnBehalf,
    ),
    officerRecordBoardCoiPaperResponse: charityCall(sdk.charityBoardRecordBoardCoiPaperResponse),
    officerRecordBoardConflictDisclosure: charityCall(
      sdk.charityBoardRecordBoardConflictDisclosure,
    ),
    officerRecordBoardConflictLink: charityCall(sdk.charityBoardRecordBoardConflictLink),
    officerRecordBoardConsentRevocation: charityCall(sdk.charityBoardRecordBoardConsentRevocation),
    officerRecordBoardConsentSignature: charityCall(sdk.charityBoardRecordBoardConsentSignature),
    officerRecordBoardDirectorsTold: charityCall(sdk.charityBoardRecordBoardDirectorsTold),
    officerRecordBoardGovernanceParameter: charityCall(
      sdk.charityBoardRecordBoardGovernanceParameter,
    ),
    officerRecordBoardMeetingAttendance: charityCall(sdk.charityBoardRecordBoardMeetingAttendance),
    officerRecordBoardMeetingPresiding: charityCall(sdk.charityBoardRecordBoardMeetingPresiding),
    officerRecordBoardMinutesApprovalSignatureOnBehalf: charityCall(
      sdk.charityBoardRecordBoardMinutesApprovalSignatureOnBehalf,
    ),
    officerRecordBoardMinutesApprovalVote: charityCall(
      sdk.charityBoardRecordBoardMinutesApprovalVote,
    ),
    officerRecordBoardMotionRecusal: charityCall(sdk.charityBoardRecordBoardMotionRecusal),
    officerRecordBoardMotionVote: charityCall(sdk.charityBoardRecordBoardMotionVote),
    officerRecordBoardPacketSharedComment: charityCall(
      sdk.charityBoardRecordBoardPacketSharedComment,
    ),
    officerRecordBoardSegmentParticipant: charityCall(
      sdk.charityBoardRecordBoardSegmentParticipant,
    ),
    officerRecordHistoricalBoardMember: charityCall(sdk.charityBoardRecordHistoricalBoardMember),
    officerReinviteBoardMeetingInvitee: charityCall(sdk.charityBoardReinviteBoardMeetingInvitee),
    officerReleaseBoardLegalHold: charityCall(sdk.charityBoardReleaseBoardLegalHold),
    officerReleaseBoardMemberAccount: charityCall(sdk.charityBoardReleaseBoardMemberAccount),
    officerRemindBoardCoiCycleRecipients: charityCall(
      sdk.charityBoardRemindBoardCoiCycleRecipients,
    ),
    officerRemoveBoardMember: charityCall(sdk.charityBoardRemoveBoardMember),
    officerRemoveBoardMotionRecusal: charityCall(sdk.charityBoardRemoveBoardMotionRecusal),
    officerRemoveBoardSegmentParticipant: charityCall(
      sdk.charityBoardRemoveBoardSegmentParticipant,
    ),
    officerReorderBoardAgenda: charityCall(sdk.charityBoardReorderBoardAgenda),
    officerReorderBoardMeetingSegments: charityCall(sdk.charityBoardReorderBoardMeetingSegments),
    officerReorderBoardPacketItems: charityCall(sdk.charityBoardReorderBoardPacketItems),
    officerRetireBoardOfficerTitle: charityCall(sdk.charityBoardRetireBoardOfficerTitle),
    officerReviseBoardWrittenConsent: charityCall(sdk.charityBoardReviseBoardWrittenConsent),
    officerScheduleRestoredBoardMeeting: charityCall(sdk.charityBoardScheduleRestoredBoardMeeting),
    officerSendBoardCoiCycleRequests: charityCall(sdk.charityBoardSendBoardCoiCycleRequests),
    // CBG-YR4-ACK, -START: a signed paper form, the board's getting-started list, and one member's
    // list — its start, change, buddy, a step's mark and its one-way close.
    officerRecordBoardPaperAcknowledgments: charityCall(
      sdk.charityBoardRecordBoardPaperAcknowledgments,
    ),
    officerSaveBoardGettingStartedList: charityCall(sdk.charityBoardSaveBoardGettingStartedList),
    officerStartBoardMemberGettingStarted: charityCall(
      sdk.charityBoardStartBoardMemberGettingStarted,
    ),
    officerSaveBoardMemberGettingStarted: charityCall(
      sdk.charityBoardSaveBoardMemberGettingStarted,
    ),
    officerChooseBoardMemberBuddy: charityCall(sdk.charityBoardChooseBoardMemberBuddy),
    officerMarkBoardMemberGettingStartedStep: charityCall(
      sdk.charityBoardMarkBoardMemberGettingStartedStep,
    ),
    officerCloseBoardMemberGettingStarted: charityCall(
      sdk.charityBoardCloseBoardMemberGettingStarted,
    ),
    // CBG-YR4-ASSESS-WEB: the board's self-assessment — its reads and its acts. Their models carry
    // no class-spelled field (contract §12.1), so the door's answer is the module's as it stands.
    officerCancelBoardSelfAssessment: charityCall(sdk.charityBoardCancelBoardSelfAssessment),
    officerCloseBoardSelfAssessment: charityCall(sdk.charityBoardCloseBoardSelfAssessment),
    officerGetBoardSelfAssessment: charityCall(sdk.charityBoardGetBoardSelfAssessment),
    officerGetBoardSelfAssessmentInstrument: charityCall(
      sdk.charityBoardGetBoardSelfAssessmentInstrument,
    ),
    officerGetBoardSelfAssessmentQuestions: charityCall(
      sdk.charityBoardGetBoardSelfAssessmentQuestions,
    ),
    officerGetBoardSelfAssessmentResults: charityCall(
      sdk.charityBoardGetBoardSelfAssessmentResults,
    ),
    officerLinkBoardSelfAssessmentPriorities: charityCall(
      sdk.charityBoardLinkBoardSelfAssessmentPriorities,
    ),
    officerListBoardSelfAssessmentInstruments: charityCall(
      sdk.charityBoardListBoardSelfAssessmentInstruments,
    ),
    officerListBoardSelfAssessments: charityCall(sdk.charityBoardListBoardSelfAssessments),
    officerMarkBoardSelfAssessmentPaperHandIn: charityCall(
      sdk.charityBoardMarkBoardSelfAssessmentPaperHandIn,
    ),
    officerOpenBoardSelfAssessment: charityCall(sdk.charityBoardOpenBoardSelfAssessment),
    officerRecordBoardSelfAssessmentPaperSheet: charityCall(
      sdk.charityBoardRecordBoardSelfAssessmentPaperSheet,
    ),
    officerRemindBoardSelfAssessmentRecipients: charityCall(
      sdk.charityBoardRemindBoardSelfAssessmentRecipients,
    ),
    officerSendBoardSelfAssessmentRequests: charityCall(
      sdk.charityBoardSendBoardSelfAssessmentRequests,
    ),
    officerUnmarkBoardSelfAssessmentPaperHandIn: charityCall(
      sdk.charityBoardUnmarkBoardSelfAssessmentPaperHandIn,
    ),
    // CBG-YR5-VIEW: the Form 990 page's read. Its response names no organization, so nothing in it
    // is re-spelled; the request's path key is.
    officerGetBoardForm990: charityCall(sdk.charityBoardGetBoardForm990),
    // CBG-YR5-SCHO: the Schedule O draft. Like the Form 990 read, its response names no organization;
    // the request's path key is re-spelled.
    officerGetBoardScheduleODraft: charityCall(sdk.charityBoardGetBoardScheduleODraft),
    // CBG-YR4-PAY and -PAY-CONSENT: a board's compensation decisions. Their models carry no
    // organization identity (each is its own twin), so the door's answer is the module's as it is.
    officerAddBoardConsentCompensationDecision: charityCall(
      sdk.charityBoardAddBoardConsentCompensationDecision,
    ),
    officerAddBoardMotionCompensationDecision: charityCall(
      sdk.charityBoardAddBoardMotionCompensationDecision,
    ),
    officerConfirmBoardCompensationDocument: charityCall(
      sdk.charityBoardConfirmBoardCompensationDocument,
    ),
    officerGetBoardCompensationDecision: charityCall(sdk.charityBoardGetBoardCompensationDecision),
    officerGetBoardCompensationDecisionDocumentDownload: charityCall(
      sdk.charityBoardGetBoardCompensationDecisionDocumentDownload,
    ),
    officerGetBoardCompensationDocumentDownload: charityCall(
      sdk.charityBoardGetBoardCompensationDocumentDownload,
    ),
    officerIssueBoardCompensationDocumentUploadUrl: charityCall(
      sdk.charityBoardIssueBoardCompensationDocumentUploadUrl,
    ),
    officerListBoardCompensationDecisions: charityCall(
      sdk.charityBoardListBoardCompensationDecisions,
    ),
    officerListBoardConsentCompensationDecisions: charityCall(
      sdk.charityBoardListBoardConsentCompensationDecisions,
    ),
    officerListBoardMeetingCompensationDecisions: charityCall(
      sdk.charityBoardListBoardMeetingCompensationDecisions,
    ),
    officerRecordBoardCompensationIndexEntry: charityCall(
      sdk.charityBoardRecordBoardCompensationIndexEntry,
    ),
    officerRemoveBoardCompensationDocument: charityCall(
      sdk.charityBoardRemoveBoardCompensationDocument,
    ),
    officerRemoveBoardCompensationIndexEntry: charityCall(
      sdk.charityBoardRemoveBoardCompensationIndexEntry,
    ),
    officerRemoveBoardConsentCompensationDecision: charityCall(
      sdk.charityBoardRemoveBoardConsentCompensationDecision,
    ),
    officerRemoveBoardMotionCompensationDecision: charityCall(
      sdk.charityBoardRemoveBoardMotionCompensationDecision,
    ),
    officerReplaceBoardConsentCompensationDecision: charityCall(
      sdk.charityBoardReplaceBoardConsentCompensationDecision,
    ),
    officerReplaceBoardMotionCompensationDecision: charityCall(
      sdk.charityBoardReplaceBoardMotionCompensationDecision,
    ),
    officerUpdateBoardCompensationDecision: charityCall(
      sdk.charityBoardUpdateBoardCompensationDecision,
    ),
    // CBG-YR5-DIST and CBG-YR5-REVIEW: a copy of the return provided to the board, and the board's
    // review linked to it. No response names the organization (each model is its own twin), so
    // nothing in them is re-spelled; the request's path key is.
    officerConfirmBoardReturnCopy: charityCall(sdk.charityBoardConfirmBoardReturnCopy),
    officerEmailBoardReturnAgain: charityCall(sdk.charityBoardEmailBoardReturnAgain),
    officerGetBoardReturnCopyUrl: charityCall(sdk.charityBoardGetBoardReturnCopyUrl),
    officerGetBoardReturnDistribution: charityCall(sdk.charityBoardGetBoardReturnDistribution),
    officerIssueBoardReturnCopyUploadUrl: charityCall(
      sdk.charityBoardIssueBoardReturnCopyUploadUrl,
    ),
    officerLinkBoardReturnReview: charityCall(sdk.charityBoardLinkBoardReturnReview),
    officerListBoardReturnDistributions: charityCall(sdk.charityBoardListBoardReturnDistributions),
    officerListBoardReturnReviewCandidates: charityCall(
      sdk.charityBoardListBoardReturnReviewCandidates,
    ),
    officerPreviewBoardReturnRecipients: charityCall(sdk.charityBoardPreviewBoardReturnRecipients),
    officerProvideBoardReturn: charityCall(sdk.charityBoardProvideBoardReturn),
    officerRecordBoardReturnPrintedCopy: charityCall(sdk.charityBoardRecordBoardReturnPrintedCopy),
    officerUnlinkBoardReturnReview: charityCall(sdk.charityBoardUnlinkBoardReturnReview),
    officerWithdrawBoardReturn: charityCall(sdk.charityBoardWithdrawBoardReturn),
    // CBG-AI5-PROPOSE: what the connected AI proposed and a person's acts on it (CBG-AI5-CLOSED: a
    // record's mark as a closed session's too), the by-hand form's file (CBG-UX11: its slot with no
    // batch too) and a record's file. No response names the organization (each model is its own
    // twin), so nothing in them is re-spelled; the request's path key is.
    officerCheckBoardProposalFile: charityCall(sdk.charityBoardCheckBoardProposalFile),
    officerConfirmBoardPastRecordBatch: charityCall(sdk.charityBoardConfirmBoardPastRecordBatch),
    officerDeleteBoardImportFileUpload: charityCall(sdk.charityBoardDeleteBoardImportFileUpload),
    officerDeleteBoardImportRecordFileUpload: charityCall(
      sdk.charityBoardDeleteBoardImportRecordFileUpload,
    ),
    officerDiscardBoardMemberProposal: charityCall(sdk.charityBoardDiscardBoardMemberProposal),
    officerDiscardBoardPastRecordBatch: charityCall(sdk.charityBoardDiscardBoardPastRecordBatch),
    officerGetBoardPastRecordBatch: charityCall(sdk.charityBoardGetBoardPastRecordBatch),
    officerGetBoardProposalFileUrl: charityCall(sdk.charityBoardGetBoardProposalFileUrl),
    officerGetBoardRecordFileUrl: charityCall(sdk.charityBoardGetBoardRecordFileUrl),
    officerIssueBoardImportFileUploadUrl: charityCall(
      sdk.charityBoardIssueBoardImportFileUploadUrl,
    ),
    officerIssueBoardImportRecordFileUploadUrl: charityCall(
      sdk.charityBoardIssueBoardImportRecordFileUploadUrl,
    ),
    officerIssueBoardProposalFileUploadUrl: charityCall(
      sdk.charityBoardIssueBoardProposalFileUploadUrl,
    ),
    officerLeaveOutBoardPastRecord: charityCall(sdk.charityBoardLeaveOutBoardPastRecord),
    officerListBoardMemberProposals: charityCall(sdk.charityBoardListBoardMemberProposals),
    officerListBoardPastRecordBatches: charityCall(sdk.charityBoardListBoardPastRecordBatches),
    officerMarkBoardPastRecordConfidentiality: charityCall(
      sdk.charityBoardMarkBoardPastRecordConfidentiality,
    ),
    // CBG-AI5-INBOX-WEB: the uploads a person makes for their AI — a file's slot, Board's check,
    // the page's list, a file opened and a removal. No response names the organization, so only
    // the request's path key is re-spelled.
    officerCheckBoardImportInboxFile: charityCall(sdk.charityBoardCheckBoardImportInboxFile),
    officerGetBoardImportInboxFileUrl: charityCall(sdk.charityBoardGetBoardImportInboxFileUrl),
    officerIssueBoardImportInboxUploadUrl: charityCall(
      sdk.charityBoardIssueBoardImportInboxUploadUrl,
    ),
    officerListBoardImportInbox: charityCall(sdk.charityBoardListBoardImportInbox),
    officerRemoveBoardImportInboxFile: charityCall(sdk.charityBoardRemoveBoardImportInboxFile),
    // CBG-UX7-WEB-OFF: the officer's to-do list. Its model carries no class-spelled field, so the
    // door's answer is the module's as it stands.
    officerGetBoardToDo: charityCall(sdk.charityBoardGetBoardToDo),
    officerSendBoardMeetingInvitations: charityCall(sdk.charityBoardSendBoardMeetingInvitations),
    officerSendBoardMinutesReminder: charityCall(sdk.charityBoardSendBoardMinutesReminder),
    officerSignBoardMinutesOnBehalf: charityCall(sdk.charityBoardSignBoardMinutesOnBehalf),
    officerSubmitBoardRecord: charityCall(sdk.charityBoardSubmitBoardRecord),
    officerUpdateBoardAgendaItem: charityCall(sdk.charityBoardUpdateBoardAgendaItem),
    officerUpdateBoardCoiCycle: charityCall(sdk.charityBoardUpdateBoardCoiCycle),
    officerUpdateBoardImportBatch: charityCall(sdk.charityBoardUpdateBoardImportBatch),
    officerUpdateBoardMeeting: charityCall(sdk.charityBoardUpdateBoardMeeting),
    officerUpdateBoardMeetingSegment: charityCall(sdk.charityBoardUpdateBoardMeetingSegment),
    officerUpdateBoardMember: charityCall(sdk.charityBoardUpdateBoardMember),
    officerUpdateBoardMotion: charityCall(sdk.charityBoardUpdateBoardMotion),
    officerUpdateBoardOfficerTitle: charityCall(sdk.charityBoardUpdateBoardOfficerTitle),
    officerUpdateBoardRecordDraft: charityCall(sdk.charityBoardUpdateBoardRecordDraft),
    officerUpdateBoardWrittenConsent: charityCall(sdk.charityBoardUpdateBoardWrittenConsent),
    officerWithdrawBoardMinutesApprovalSignatures: charityCall(
      sdk.charityBoardWithdrawBoardMinutesApprovalSignatures,
    ),
    officerWithdrawBoardMotion: charityCall(sdk.charityBoardWithdrawBoardMotion),
    officerWithdrawBoardWrittenConsent: charityCall(sdk.charityBoardWithdrawBoardWrittenConsent),
    officerWriteBoardMinutesDraft: charityCall(sdk.charityBoardWriteBoardMinutesDraft),
    officerGetBoardCommitteeOptions: charityQueryOptions(
      query.charityBoardGetBoardCommitteeOptions,
    ),
    officerGetBoardCommitteeSuggestionsOptions: charityQueryOptions(
      query.charityBoardGetBoardCommitteeSuggestionsOptions,
    ),
    officerGetBoardMeetingOptions: charityQueryOptions(query.charityBoardGetBoardMeetingOptions),
    officerGetBoardMeetingPresidingOptions: charityQueryOptions(
      query.charityBoardGetBoardMeetingPresidingOptions,
    ),
    officerGetBoardMinutesOptions: charityQueryOptions(query.charityBoardGetBoardMinutesOptions),
    officerGetBoardOfficerDutiesOptions: charityQueryOptions(
      query.charityBoardGetBoardOfficerDutiesOptions,
    ),
    officerGetBoardPacketOptions: charityQueryOptions(query.charityBoardGetBoardPacketOptions),
    officerListBoardAgendaItemsOptions: charityQueryOptions(
      query.charityBoardListBoardAgendaItemsOptions,
    ),
    officerListBoardAgendaTemplatesOptions: charityQueryOptions(
      query.charityBoardListBoardAgendaTemplatesOptions,
    ),
    officerListBoardAttendanceIntentsOptions: charityQueryOptions(
      query.charityBoardListBoardAttendanceIntentsOptions,
    ),
    officerListBoardCarryForwardItemsOptions: charityQueryOptions(
      query.charityBoardListBoardCarryForwardItemsOptions,
    ),
    officerListBoardCarryForwardMotionsOptions: charityQueryOptions(
      query.charityBoardListBoardCarryForwardMotionsOptions,
    ),
    officerListBoardCommitteeSourcesOptions: charityQueryOptions(
      query.charityBoardListBoardCommitteeSourcesOptions,
    ),
    officerListBoardCommitteesOptions: charityQueryOptions(
      query.charityBoardListBoardCommitteesOptions,
    ),
    officerListBoardConflictLinksOptions: charityQueryOptions(
      query.charityBoardListBoardConflictLinksOptions,
    ),
    officerListBoardConflictRegisterOptions: charityQueryOptions(
      query.charityBoardListBoardConflictRegisterOptions,
    ),
    officerListBoardConflictRegisterQueryKey: charityQueryKey(
      query.charityBoardListBoardConflictRegisterQueryKey,
    ),
    officerListBoardConflictSuggestionsOptions: charityQueryOptions(
      query.charityBoardListBoardConflictSuggestionsOptions,
    ),
    officerListBoardContemporaneityOptions: charityQueryOptions(
      query.charityBoardListBoardContemporaneityOptions,
    ),
    officerListBoardDocumentsOptions: charityQueryOptions(
      query.charityBoardListBoardDocumentsOptions,
    ),
    officerListBoardEngagementOptions: charityQueryOptions(
      query.charityBoardListBoardEngagementOptions,
    ),
    officerListBoardGovernanceParametersOptions: charityQueryOptions(
      query.charityBoardListBoardGovernanceParametersOptions,
    ),
    officerListBoardLegalHoldsOptions: charityQueryOptions(
      query.charityBoardListBoardLegalHoldsOptions,
    ),
    officerListBoardMeetingAttendanceOptions: charityQueryOptions(
      query.charityBoardListBoardMeetingAttendanceOptions,
    ),
    officerListBoardApprovableMinutesOptions: charityQueryOptions(
      query.charityBoardListBoardApprovableMinutesOptions,
    ),
    officerListBoardMeetingSegmentsOptions: charityQueryOptions(
      query.charityBoardListBoardMeetingSegmentsOptions,
    ),
    officerListBoardMeetingsOptions: charityQueryOptions(
      query.charityBoardListBoardMeetingsOptions,
    ),
    officerListBoardMembersOptions: charityQueryOptions(query.charityBoardListBoardMembersOptions),
    // CBG-YR4-START: the board's list, and one member's list.
    officerGetBoardGettingStartedListOptions: charityQueryOptions(
      query.charityBoardGetBoardGettingStartedListOptions,
    ),
    officerGetBoardMemberGettingStartedOptions: charityQueryOptions(
      query.charityBoardGetBoardMemberGettingStartedOptions,
    ),
    // CBG-YR3-TERMS: the board's own director classes. CBG-YR3-ANNUAL: the terms ending by an
    // annual meeting.
    officerListBoardDirectorClassesOptions: charityQueryOptions(
      query.charityBoardListBoardDirectorClassesOptions,
    ),
    officerGetBoardTermSuggestionsOptions: charityQueryOptions(
      query.charityBoardGetBoardTermSuggestionsOptions,
    ),
    officerListBoardMotionsOptions: charityQueryOptions(query.charityBoardListBoardMotionsOptions),
    officerListBoardOfficerTitlesOptions: charityQueryOptions(
      query.charityBoardListBoardOfficerTitlesOptions,
    ),
    officerListBoardPacketCommentsOptions: charityQueryOptions(
      query.charityBoardListBoardPacketCommentsOptions,
    ),
    officerListBoardPacketDistributionsOptions: charityQueryOptions(
      query.charityBoardListBoardPacketDistributionsOptions,
    ),
    officerListBoardPacketsOptions: charityQueryOptions(query.charityBoardListBoardPacketsOptions),
    officerListBoardRecordsOptions: charityQueryOptions(query.charityBoardListBoardRecordsOptions),
    officerListBoardRetentionPoliciesOptions: charityQueryOptions(
      query.charityBoardListBoardRetentionPoliciesOptions,
    ),
  };
  return members as BoardOperations;
}

/**
 * Board's runtime in this console: the port above, the director port, the `/board` mount and the
 * class words. ONE object, spread by `app.tsx` and by the console test harness alike, so the wiring
 * every console test exercises IS the wiring that ships; `board-host-words.test.tsx` pins it through
 * the shipped `App`.
 */
/**
 * CBG-AI5-PROPOSE: the console's active organization and the person's effective permissions there,
 * off the console's one standing read, for the two counts Board puts in this console's menu (the
 * menu draws outside Board's officer host). Nothing outside the console's provider.
 */
function useCharityBoardStanding(): BoardStanding {
  const standing = useOptionalCharityConsole();
  return { orgRef: standing?.orgRef ?? null, permissions: standing?.read.permissions ?? null };
}

export const CHARITY_BOARD_RUNTIME: BoardRuntime = {
  operations: createCharityBoardOperations(),
  directorOperations: createOrgBoardDirectorOperations(),
  connectionOperations: createOrgConnectionOperations(),
  basePath: '/board',
  vocab: CHARITY_BOARD_VOCAB,
  disclaimerTarget: charityBoardDisclaimerTarget,
  // A charity answers Form 990's Part VI: the compensation decisions ask for its line-15 answer.
  answersForm990: true,
  useStanding: useCharityBoardStanding,
};
